import { AlertCircleIcon, CheckIcon, InfoCircleIcon, WarningTriangleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { AlertDescription } from '../alert/alert.description';
import { AlertTitle } from '../alert/alert.title';
import { AlertLevel } from '../alert/types';

const toastIcons: Record<AlertLevel, React.ReactNode> = {
  [AlertLevel.Default]: <InfoCircleIcon size={20} className="text-quinary" />,
  [AlertLevel.Info]: <InfoCircleIcon size={20} className="text-quinary" />,
  [AlertLevel.Error]: <AlertCircleIcon size={20} className="text-error-400" />,
  [AlertLevel.Success]: <CheckIcon size={20} className="text-success-400" />,
  [AlertLevel.Warning]: <WarningTriangleIcon size={20} className="text-warning-400" />,
};

export interface ToastData {
  id: string;
  title: string;
  description?: string;
  level?: AlertLevel;
  timeout?: number;
  /** One follow-up the toast offers, e.g. "View scores"; pressing it runs `onPress` and dismisses the toast. */
  action?: { label: string; onPress: () => void };
}

interface ToastContextType {
  /** Shows a toast; returns its id, e.g. to dismiss a "Preparing…" toast once the work is done. */
  addToast: (toast: Omit<ToastData, 'id'>) => string;
  /** Removes a toast right away, before its timeout. Unknown or already gone ids are ignored. */
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_FADE_DURATION_MS = 1000;
const DEFAULT_TOAST_TIMEOUT_MS = 5000;

/** The pending timer of a toast: either counting down to its fade, or the fade itself (which always finishes). */
interface ToastTimer {
  timerId: number;
  fading: boolean;
}

type HoldReason = 'hovered' | 'focused';

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [fadingIds, setFadingIds] = useState<ReadonlySet<string>>(new Set());
  const timersRef = useRef<Map<string, ToastTimer>>(new Map());
  // Toasts the pointer is over or focus is inside; their countdown waits until both leave
  const holdsRef = useRef<Map<string, Record<HoldReason, boolean>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer.timerId);
      timersRef.current.delete(id);
    }
    holdsRef.current.delete(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
    setFadingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const fadeOutToast = useCallback(
    (id: string) => {
      setFadingIds((prev) => new Set(prev).add(id));
      const timerId = setTimeout(() => removeToast(id), TOAST_FADE_DURATION_MS);
      timersRef.current.set(id, { timerId: timerId as unknown as number, fading: true });
    },
    [removeToast],
  );

  const startCountdown = useCallback(
    (id: string, timeout: number) => {
      const timerId = setTimeout(() => fadeOutToast(id), timeout);
      timersRef.current.set(id, { timerId: timerId as unknown as number, fading: false });
    },
    [fadeOutToast],
  );

  /** Stops the countdown while the user is on the toast, so it can't vanish under the pointer or focus. */
  const holdToast = useCallback((id: string, reason: HoldReason) => {
    const holds = holdsRef.current.get(id) ?? { hovered: false, focused: false };
    holds[reason] = true;
    holdsRef.current.set(id, holds);
    const timer = timersRef.current.get(id);
    if (timer && !timer.fading) {
      clearTimeout(timer.timerId);
      timersRef.current.delete(id);
    }
  }, []);

  /** Restarts the full countdown once the pointer and focus have both left the toast. */
  const releaseToast = useCallback(
    (id: string, reason: HoldReason, timeout: number) => {
      const holds = holdsRef.current.get(id);
      if (!holds) return;
      holds[reason] = false;
      if (holds.hovered || holds.focused) return;
      holdsRef.current.delete(id);
      if (!timersRef.current.has(id)) startCountdown(id, timeout);
    },
    [startCountdown],
  );

  const addToast = useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: ToastData = {
        ...toast,
        id,
        timeout: toast.timeout || DEFAULT_TOAST_TIMEOUT_MS,
      };

      setToasts((prev) => [...prev, newToast]);
      startCountdown(id, newToast.timeout ?? DEFAULT_TOAST_TIMEOUT_MS);
      return id;
    },
    [startCountdown],
  );

  useEffect(() => {
    return () => {
      for (const { timerId } of timersRef.current.values()) {
        clearTimeout(timerId);
      }
      timersRef.current.clear();
    };
  }, []);

  const contextValue = React.useMemo(() => ({ addToast, dismissToast: removeToast }), [addToast, removeToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm" aria-label="Notifications">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            onMouseEnter={() => holdToast(toast.id, 'hovered')}
            onMouseLeave={() => releaseToast(toast.id, 'hovered', toast.timeout ?? DEFAULT_TOAST_TIMEOUT_MS)}
            onFocus={() => holdToast(toast.id, 'focused')}
            onBlur={() => releaseToast(toast.id, 'focused', toast.timeout ?? DEFAULT_TOAST_TIMEOUT_MS)}
            className={cx(
              fadingIds.has(toast.id)
                ? 'animate-out fade-out fill-mode-forwards duration-1000 motion-reduce:animate-none'
                : 'animate-in slide-in-from-right-full duration-300',
            )}
          >
            <div className="flex items-start gap-8 rounded-xl bg-foreground-primary p-8 shadow-lg" role="alert">
              <div className="h-fit w-fit shrink-0">{toastIcons[toast.level || AlertLevel.Info]}</div>
              <div className="flex flex-col gap-2 justify-center">
                <AlertTitle className="text-primary-on-brand">{toast.title}</AlertTitle>
                {toast.description && <AlertDescription className="text-quinary">{toast.description}</AlertDescription>}
                {toast.action && (
                  <AriaButton
                    className={cx(
                      'w-fit text-left text-sm font-semibold text-primary-on-brand underline-offset-2 outline-none',
                      'data-[hovered]:underline data-[focus-visible]:underline',
                    )}
                    onPress={() => {
                      toast.action?.onPress();
                      removeToast(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </AriaButton>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
